import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Users, CalendarClock, Globe, RefreshCw, LogOut } from "lucide-react";
import { fetchVisits, fetchVisitStats } from "../api";
import { useAuth } from "../context/AuthContext";

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const deviceOf = (ua = "") => {
  if (/mobile/i.test(ua)) return "Mobile";
  if (/tablet|ipad/i.test(ua)) return "Tablet";
  return "Desktop";
};

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-[#141210] border border-[#2c2822] rounded-lg p-6">
    <div className="flex items-center gap-3 mb-2">
      <Icon size={18} className="text-[#c9a24b]" />
      <span className="text-[#a29a8c] text-xs tracking-[0.15em] uppercase">{label}</span>
    </div>
    <p className="font-display text-4xl text-[#f4efe4]">{value}</p>
  </div>
);

const AdminVisitors = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [visits, setVisits] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [v, s] = await Promise.all([fetchVisits(100), fetchVisitStats()]);
      setVisits(v);
      setStats(s);
    } catch (e) {
      // errors surface globally
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    timer.current = setInterval(load, 8000); // real-time refresh
    return () => clearInterval(timer.current);
  }, [load]);

  const doLogout = () => {
    clearInterval(timer.current);
    logout();
    navigate("/login");
  };

  return (
    <div className="max-w-[1280px] mx-auto px-6 py-16">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <div>
          <p className="text-[#c9a24b] text-sm tracking-[0.3em] uppercase mb-2">Admin</p>
          <h1 className="font-display text-4xl text-[#f4efe4]">Site Visitors</h1>
          <div className="flex gap-4 mt-3 text-xs tracking-[0.15em] uppercase">
            <a href="/admin/orders" className="text-[#a29a8c] hover:text-[#c9a24b] pb-1">Orders</a>
            <a href="/admin/inventory" className="text-[#a29a8c] hover:text-[#c9a24b] pb-1">Inventory</a>
            <a href="/admin/reviews" className="text-[#a29a8c] hover:text-[#c9a24b] pb-1">Reviews</a>
            <a href="/admin/visitors" className="text-[#c9a24b] border-b border-[#c9a24b] pb-1">Visitors</a>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-2 text-[#a29a8c] text-xs">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /> Live
          </span>
          <button onClick={load} className="btn-outline-gold px-4 py-2 rounded-full text-xs tracking-[0.15em] uppercase inline-flex items-center gap-2">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button onClick={doLogout} className="text-[#a29a8c] hover:text-[#c9a24b] transition-colors inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em]">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          <StatCard icon={Users} label="Total Visits" value={stats.total} />
          <StatCard icon={CalendarClock} label="Today" value={stats.today} />
          <StatCard icon={Globe} label="Top Country" value={stats.top_countries?.[0]?._id || "—"} />
          <StatCard icon={MapPin} label="Top City" value={stats.top_cities?.[0]?._id?.city || "—"} />
        </div>
      )}

      <div className="bg-[#141210] border border-[#2c2822] rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-[#2c2822] flex justify-between items-center">
          <h2 className="font-display text-xl text-[#f4efe4]">Recent Visits</h2>
          <span className="text-[#6b6459] text-xs">Auto-refreshing every 8s</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[#6b6459] text-xs tracking-[0.12em] uppercase">
                <th className="text-left font-medium px-6 py-3">When</th>
                <th className="text-left font-medium px-6 py-3">Location</th>
                <th className="text-left font-medium px-6 py-3">Page</th>
                <th className="text-left font-medium px-6 py-3">Device</th>
                <th className="text-left font-medium px-6 py-3">Referrer</th>
              </tr>
            </thead>
            <tbody>
              {visits.length === 0 ? (
                <tr><td colSpan={5} className="px-6 py-10 text-center text-[#a29a8c]">No visits recorded yet.</td></tr>
              ) : (
                visits.map((v) => (
                  <tr key={v.id} className="border-t border-[#2c2822] text-[#c8c0b2] hover:bg-[#1b1815] transition-colors">
                    <td className="px-6 py-3 whitespace-nowrap">{timeAgo(v.timestamp)}</td>
                    <td className="px-6 py-3">
                      {v.city || v.country ? (
                        <span className="inline-flex items-center gap-1.5">
                          <MapPin size={13} className="text-[#c9a24b]" />
                          {[v.city, v.region, v.country].filter(Boolean).join(", ")}
                        </span>
                      ) : (
                        <span className="text-[#6b6459]">Unknown</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-[#f4efe4]">{v.path}</td>
                    <td className="px-6 py-3">{deviceOf(v.user_agent)}</td>
                    <td className="px-6 py-3 max-w-[220px] truncate text-[#6b6459]">{v.referrer || "Direct"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-[#6b6459] text-xs mt-4">Location is approximate (IP-based). Bookmark this private page: /admin/visitors</p>
    </div>
  );
};

export default AdminVisitors;
